/**
 * Settings Service — LOCAL (frontend-only) implementation.
 *
 * Stores all admin settings under one localStorage key: 'agrocast_admin_settings'.
 * Each section is a top-level property; sections are loaded and saved independently
 * so only the changed section is written, not the entire blob.
 *
 * Shape:
 *   { profile, organization, branding, regional, notifications, security }
 *
 * Contract (identical to what a real API surface would expose):
 *   getProfile()                   → Promise<profile>
 *   updateProfile(data)            → Promise<profile>
 *   getOrganization()              → Promise<organization>
 *   updateOrganization(data)       → Promise<organization>
 *   getBranding()                  → Promise<branding>
 *   updateBranding(data)           → Promise<branding>
 *   getRegional()                  → Promise<regional>
 *   updateRegional(data)           → Promise<regional>
 *   getNotifications()             → Promise<notifications>
 *   updateNotifications(data)      → Promise<notifications>
 *   getSecurity()                  → Promise<security>
 *   updateSecurity(data)           → Promise<security>
 *   changePassword(data)           → Promise<{ success: true }>
 *   uploadAvatar(file)             → Promise<{ avatarUrl: string }>
 *   uploadLogo(file, type)         → Promise<{ url: string }>
 */

const STORE_KEY = 'agrocast_admin_settings'
const LATENCY   = 120

// ── Defaults ──────────────────────────────────────────────────────────────────

const DEFAULTS = {
  profile: {
    firstName:  'Admin',
    lastName:   'User',
    email:      'admin@agrocast.app',
    phone:      '',
    bio:        '',
    avatarUrl:  null,
    role:       'admin',
    jobTitle:   'Administrator',
    department: '',
    joinedAt:   new Date().toISOString(),
  },
  organization: {
    name:        'After Summit',
    website:     'https://agrocast.app',
    email:       'contact@agrocast.app',
    phone:       '',
    address:     '',
    city:        'Pune',
    state:       'Maharashtra',
    country:     'India',
    zipCode:     '',
    description: 'Premier agricultural event management platform connecting farmers and professionals across India.',
    logoUrl:     null,
    socialLinks: {
      twitter:  '',
      linkedin: '',
      facebook: '',
      youtube:  '',
    },
  },
  branding: {
    appName:    'After Summit',
    tagline:    'Co-Powered by AgroCast Analytics',
    logoUrl:    null,
    faviconUrl: null,
  },
  regional: {
    timezone:     'Asia/Kolkata',
    language:     'en-IN',
    dateFormat:   'DD/MM/YYYY',
    timeFormat:   '12h',
    currency:     'INR',
    weekStart:    'monday',
    numberFormat: 'en-IN',
  },
  notifications: {
    emailNewRegistration:    true,
    emailRegistrationUpdate: true,
    emailEventPublished:     false,
    emailEventUpdated:       true,
    emailWeeklyDigest:       true,
    emailSecurityAlerts:     true,
    smsAlerts:               false,
    pushNotifications:       true,
    digestFrequency:         'daily',
    digestTime:              '08:00',
    notifyOnLogin:           true,
  },
  security: {
    twoFactorEnabled:         false,
    twoFactorMethod:          'app',
    sessionTimeoutMinutes:    60,
    loginNotifications:       true,
    maxLoginAttempts:         5,
    lockoutDurationMinutes:   15,
    requireEmailVerification: true,
    allowRegistrations:       true,
    ipWhitelist:              '',
    maintenanceMode:          false,
    maintenanceMessage:       'We are currently performing scheduled maintenance. Please check back soon.',
    maintenanceAllowAdmins:   true,
  },
}

// ── Persistence ───────────────────────────────────────────────────────────────

function readStore() {
  try {
    const raw = localStorage.getItem(STORE_KEY)
    return raw ? JSON.parse(raw) : {}
  } catch { return {} }
}

function readSection(section) {
  const store = readStore()
  return { ...DEFAULTS[section], ...(store[section] ?? {}) }
}

function writeSection(section, data) {
  try {
    const store = readStore()
    store[section] = data
    localStorage.setItem(STORE_KEY, JSON.stringify(store))
  } catch (err) {
    if (err?.name === 'QuotaExceededError') {
      throw Object.assign(new Error('Storage limit reached.'), { status: 507 })
    }
    throw err
  }
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const delay = (ms) => new Promise((r) => setTimeout(r, ms))

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload  = () => resolve(reader.result)
    reader.onerror = () => reject(Object.assign(new Error('Failed to read image.'), { status: 400 }))
    reader.readAsDataURL(file)
  })
}

// ── Service ───────────────────────────────────────────────────────────────────

const settingsLocalService = {

  // ── Profile ──────────────────────────────────────────────────────────────────

  async getProfile() {
    await delay(LATENCY)
    return readSection('profile')
  },

  async updateProfile(data) {
    await delay(LATENCY)
    const current = readSection('profile')
    const updated = { ...current, ...data, updatedAt: new Date().toISOString() }
    writeSection('profile', updated)
    return updated
  },

  async uploadAvatar(file) {
    await delay(LATENCY)
    const url = await fileToDataUrl(file)
    const current = readSection('profile')
    const updated = { ...current, avatarUrl: url, updatedAt: new Date().toISOString() }
    writeSection('profile', updated)
    return { avatarUrl: url }
  },

  async changePassword({ currentPassword, newPassword }) {
    await delay(LATENCY * 2)
    // Frontend-only: just simulate the call.
    if (!currentPassword) throw Object.assign(new Error('Current password is required.'), { status: 400 })
    if (newPassword.length < 8) throw Object.assign(new Error('Password too short.'), { status: 400 })
    return { success: true }
  },

  // ── Organization ─────────────────────────────────────────────────────────────

  async getOrganization() {
    await delay(LATENCY)
    return readSection('organization')
  },

  async updateOrganization(data) {
    await delay(LATENCY)
    const current = readSection('organization')
    const updated = { ...current, ...data, updatedAt: new Date().toISOString() }
    writeSection('organization', updated)
    return updated
  },

  async uploadLogo(file, type = 'logo') {
    await delay(LATENCY)
    const url = await fileToDataUrl(file)
    // type === 'branding'  → logoUrl   in branding section
    // type === 'favicon'   → faviconUrl in branding section
    // anything else        → logoUrl   in organization section
    const section = (type === 'branding' || type === 'favicon') ? 'branding' : 'organization'
    const key     = type === 'favicon' ? 'faviconUrl' : 'logoUrl'
    const current = readSection(section)
    const updated = { ...current, [key]: url, updatedAt: new Date().toISOString() }
    writeSection(section, updated)
    return { url }
  },

  // ── Branding ─────────────────────────────────────────────────────────────────

  async getBranding() {
    await delay(LATENCY)
    return readSection('branding')
  },

  async updateBranding(data) {
    await delay(LATENCY)
    const current = readSection('branding')
    const updated = { ...current, ...data, updatedAt: new Date().toISOString() }
    writeSection('branding', updated)
    return updated
  },

  // ── Regional ─────────────────────────────────────────────────────────────────

  async getRegional() {
    await delay(LATENCY)
    return readSection('regional')
  },

  async updateRegional(data) {
    await delay(LATENCY)
    const current = readSection('regional')
    const updated = { ...current, ...data, updatedAt: new Date().toISOString() }
    writeSection('regional', updated)
    return updated
  },

  // ── Notifications ─────────────────────────────────────────────────────────────

  async getNotifications() {
    await delay(LATENCY)
    return readSection('notifications')
  },

  async updateNotifications(data) {
    await delay(LATENCY)
    const current = readSection('notifications')
    const updated = { ...current, ...data, updatedAt: new Date().toISOString() }
    writeSection('notifications', updated)
    return updated
  },

  // ── Security ─────────────────────────────────────────────────────────────────

  async getSecurity() {
    await delay(LATENCY)
    return readSection('security')
  },

  async updateSecurity(data) {
    await delay(LATENCY)
    const current = readSection('security')
    const updated = { ...current, ...data, updatedAt: new Date().toISOString() }
    writeSection('security', updated)
    return updated
  },
}

export default settingsLocalService
